import React, { useCallback, useMemo, useRef, useState } from "react";
import { ExternalLink, Printer, X } from "lucide-react";
import { atlasColors } from "./atlasTheme";
import type { AtlasLanguage } from "../../translations";
import type { EvidenceItemV2, SurgicalPlanV2, SurgicalTechniqueV2 } from "../../utils/surgicalPlanV2";
import { SurgicalPlanReportTemplate } from "./SurgicalPlanReportTemplate";

type Props = {
  language: AtlasLanguage;
  plan: SurgicalPlanV2;
  notice?: string | null;
  patientName?: string;
  doctorName?: string;
  doctorCrm?: string;
};

type TabKey =
  | "conservative"
  | "technique-1"
  | "technique-2"
  | "postoperative"
  | "plan-b"
  | "differentials"
  | "evidence";

const sectionTitleClass = "text-[11px] font-black uppercase tracking-[0.22em] text-cyan-300";

const copy = {
  pt: {
    diagnosis: "Diagnostico",
    location: "Localizacao anatomica",
    ao: "Classificacao AO/OTA",
    conservative: "Tratamento Conservador",
    technique1: "Tecnica 1",
    technique2: "Tecnica 2",
    postoperative: "Pos-operatorio",
    planB: "Plano B",
    differentials: "Diagnosticos diferenciais",
    evidence: "Evidencias cientificas",
    indication: "Indicacao",
    when: "Quando escolher",
    positioning: "Posicionamento",
    approach: "Via de acesso",
    steps: "Passo a passo cirurgico",
    materials: "Materiais",
    advantages: "Vantagens",
    risks: "Limitacoes / riscos",
    complications: "Complicacoes possiveis",
    healing: "Tempo medio de consolidacao",
    relevance: "Relevancia",
  },
  es: {
    diagnosis: "Diagnostico",
    location: "Localizacion anatomica",
    ao: "Clasificacion AO/OTA",
    conservative: "Tratamiento conservador",
    technique1: "Tecnica 1",
    technique2: "Tecnica 2",
    postoperative: "Posoperatorio",
    planB: "Plan B",
    differentials: "Diagnosticos diferenciales",
    evidence: "Evidencias cientificas",
    indication: "Indicacion",
    when: "Cuando elegir",
    positioning: "Posicionamiento",
    approach: "Via de abordaje",
    steps: "Tecnica paso a paso",
    materials: "Materiales",
    advantages: "Ventajas",
    risks: "Limitaciones / riesgos",
    complications: "Complicaciones posibles",
    healing: "Tiempo medio de consolidacion",
    relevance: "Relevancia",
  },
  en: {
    diagnosis: "Diagnosis",
    location: "Anatomical location",
    ao: "AO/OTA classification",
    conservative: "Conservative Treatment",
    technique1: "Technique 1",
    technique2: "Technique 2",
    postoperative: "Postoperative Care",
    planB: "Plan B",
    differentials: "Differentials",
    evidence: "Scientific Evidence",
    indication: "Indication",
    when: "When to choose",
    positioning: "Positioning",
    approach: "Approach",
    steps: "Step-by-step technique",
    materials: "Materials",
    advantages: "Advantages",
    risks: "Limitations / risks",
    complications: "Possible complications",
    healing: "Average healing time",
    relevance: "Relevance",
  },
};

const labelsFor = (language: AtlasLanguage) => copy[language === "es" ? "es" : language === "en" ? "en" : "pt"];

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={`${item}-${index}`} className="flex gap-2 text-[13px] leading-normal text-slate-100">
          <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Section({ title, items, wide = false }: { title: string; items: string[]; wide?: boolean }) {
  return (
    <section className={`rounded-md border border-cyan-400/15 bg-slate-950/42 p-4 ${wide ? "lg:col-span-2" : ""}`}>
      <h4 className={sectionTitleClass}>{title}</h4>
      <div className="mt-3">
        <BulletList items={items} />
      </div>
    </section>
  );
}

function EvidenceCards({ items, relevanceLabel }: { items: EvidenceItemV2[]; relevanceLabel: string }) {
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {items.map((item, index) => (
        <article key={`${item.title}-${index}`} className="rounded-md border border-cyan-400/15 bg-slate-950/42 p-4">
          <div className="flex items-start justify-between gap-3">
            <h4 className="text-[14px] font-bold leading-snug text-slate-50">{item.title}</h4>
            {item.url ? (
              <a className="shrink-0 rounded-full border border-cyan-300/35 p-2 text-cyan-200 hover:bg-cyan-300/10" href={item.url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : null}
          </div>
          <p className="mt-2 text-[12px] leading-relaxed text-slate-300">
            {[item.authors, item.journal, item.year].filter(Boolean).join(" • ")}
          </p>
          {item.doi ? <p className="mt-2 text-[12px] text-cyan-200">DOI: {item.doi}</p> : null}
          <p className="mt-3 text-[12px] leading-relaxed text-slate-200">
            <span className="font-bold text-cyan-200">{relevanceLabel}: </span>
            {item.relevance}
          </p>
        </article>
      ))}
    </div>
  );
}

function TechniquePanel({ technique, labels }: { technique: SurgicalTechniqueV2; labels: ReturnType<typeof labelsFor> }) {
  return (
    <div className="rounded-md border border-cyan-400/20 bg-[#020817] shadow-[0_30px_90px_rgba(0,0,0,0.35)]">
      <div className="border-b border-cyan-400/15 px-5 py-4">
        <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-300">{labels.technique1}</p>
        <h3 className="mt-1 text-[20px] font-black uppercase tracking-[0.14em] text-cyan-200">{technique.title}</h3>
      </div>
      <div className="grid gap-3 p-4 lg:grid-cols-2">
        <Section title={labels.indication} items={technique.indication} />
        <Section title={labels.when} items={technique.whenToChoose} />
        <Section title={labels.positioning} items={technique.positioning} />
        <Section title={labels.approach} items={technique.surgicalApproach} />
        <Section title={labels.steps} items={technique.stepByStepTechnique} wide />
        <Section title={labels.materials} items={technique.implantsAndMaterials} wide />
        <Section title={labels.advantages} items={technique.advantages} />
        <Section title={labels.risks} items={technique.limitationsAndRisks} />
        <Section title={labels.complications} items={technique.possibleComplications} />
        <Section title={labels.healing} items={technique.consolidationTime} />
        <Section title={labels.planB} items={technique.planB} wide />
        {technique.evidence.length > 0 ? (
          <section className="lg:col-span-2">
            <h4 className={sectionTitleClass}>{labels.evidence}</h4>
            <div className="mt-3">
              <EvidenceCards items={technique.evidence} relevanceLabel={labels.relevance} />
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

const printCopy = {
  pt: { print: "Imprimir / Compartilhar", close: "Fechar", printBtn: "Imprimir" },
  en: { print: "Print / Share", close: "Close", printBtn: "Print" },
  es: { print: "Imprimir / Compartir", close: "Cerrar", printBtn: "Imprimir" },
};

export function SurgicalPlanningPremiumV2({ language, plan, notice, patientName, doctorName, doctorCrm }: Props) {
  const labels = labelsFor(language);
  const tabs = useMemo(() => {
    const base: Array<{ key: TabKey; label: string }> = [];
    if (plan.managementDecision.type === "conservative" || plan.managementDecision.type === "indeterminate") {
      base.push({ key: "conservative", label: labels.conservative });
    }
    if (plan.managementDecision.type === "surgical" || plan.managementDecision.type === "indeterminate") {
      base.push({ key: "technique-1", label: labels.technique1 });
      base.push({ key: "technique-2", label: labels.technique2 });
    }
    base.push(
      { key: "postoperative", label: labels.postoperative },
      { key: "plan-b", label: labels.planB },
      { key: "differentials", label: labels.differentials },
      { key: "evidence", label: labels.evidence }
    );
    return base;
  }, [labels, plan.managementDecision.type]);

  const [activeTab, setActiveTab] = useState<TabKey>(tabs[0]?.key || "technique-1");
  const active = tabs.some((tab) => tab.key === activeTab) ? activeTab : tabs[0]?.key;
  const globalEvidence = [...plan.globalEvidence, ...plan.surgicalTechniques.flatMap((item) => item.evidence)];

  const [showReport, setShowReport] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const pc = printCopy[language === "es" ? "es" : language === "en" ? "en" : "pt"];
  const generatedAt = new Date().toLocaleDateString(
    language === "en" ? "en-US" : language === "es" ? "es-ES" : "pt-BR",
    { day: "2-digit", month: "2-digit", year: "numeric" }
  );

  const handlePrint = useCallback(() => {
    const content = reportRef.current;
    if (!content) return;
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>Plano Cirúrgico — OrthoPrecision</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; background: #fff; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 20mm 18mm; }
        }
      </style>
    </head><body>${content.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  }, []);

  return (
    <>
    {/* Modal de preview do relatório */}
    {showReport && (
      <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/90 backdrop-blur-sm">
        {/* Toolbar */}
        <div className="flex items-center justify-between border-b border-slate-700/60 bg-slate-900 px-5 py-3 shrink-0">
          <p className="text-sm font-bold text-slate-200">Plano Cirúrgico — OrthoPrecision</p>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-600 px-4 py-2 text-sm font-bold text-white hover:bg-cyan-500 transition-colors"
            >
              <Printer className="h-4 w-4" />
              {pc.printBtn}
            </button>
            <button
              onClick={() => setShowReport(false)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-bold text-slate-200 hover:bg-slate-700 transition-colors"
            >
              <X className="h-4 w-4" />
              {pc.close}
            </button>
          </div>
        </div>
        {/* Preview */}
        <div className="flex-1 overflow-auto bg-slate-200 p-6">
          <div className="shadow-2xl">
            <SurgicalPlanReportTemplate
              ref={reportRef}
              plan={plan}
              language={language}
              patientName={patientName}
              doctorName={doctorName}
              doctorCrm={doctorCrm}
              generatedAt={generatedAt}
            />
          </div>
        </div>
      </div>
    )}

    <div className="rounded-md border border-cyan-400/20 bg-[#020817] p-4 shadow-[0_30px_90px_rgba(0,0,0,0.38)]">
      {notice ? (
        <div className="mb-4 rounded-md border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-[13px] text-amber-100">
          {notice}
        </div>
      ) : null}

      <header className="border-b border-cyan-400/15 pb-4">
        <div className="grid gap-3 lg:grid-cols-3">
          <div>
            <p className={sectionTitleClass}>{labels.diagnosis}</p>
            <p className="mt-2 text-[17px] font-bold leading-snug text-slate-50">{plan.diagnosis}</p>
          </div>
          <div>
            <p className={sectionTitleClass}>{labels.location}</p>
            <p className="mt-2 text-[17px] font-bold leading-snug text-slate-50">{plan.anatomicalLocation}</p>
          </div>
          <div>
            <p className={sectionTitleClass}>{labels.ao}</p>
            <p className="mt-2 text-[17px] font-bold leading-snug text-slate-50">{plan.aoOtaClassification || "AO/OTA nao definido pela imagem disponivel"}</p>
          </div>
        </div>
        {/* Botão de impressão */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setShowReport(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] text-cyan-300 transition-all hover:bg-cyan-500/20 hover:border-cyan-400/50"
          >
            <Printer className="h-4 w-4" />
            {pc.print}
          </button>
        </div>
      </header>

      <nav className="mt-4 flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className="shrink-0 rounded-full border px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] transition"
            style={{
              borderColor: active === tab.key ? atlasColors.accent : "rgba(34,211,238,0.22)",
              background: active === tab.key ? "rgba(8,145,178,0.24)" : "rgba(15,23,42,0.48)",
              color: active === tab.key ? "#e0faff" : "#8bd8e8",
              boxShadow: active === tab.key ? "0 0 24px rgba(34,211,238,0.16)" : "none",
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="mt-4">
        {active === "conservative" ? (
          <div className="grid gap-3 lg:grid-cols-2">
            <Section title={labels.indication} items={plan.conservativeTreatment.indication} />
            <Section title="Protecao / imobilizacao" items={plan.conservativeTreatment.immobilizationOrProtection} />
            <Section title="Medicacao" items={plan.conservativeTreatment.medication} />
            <Section title="Reabilitacao" items={plan.conservativeTreatment.rehabilitation} />
            <Section title="Seguimento" items={plan.conservativeTreatment.followUp} />
            <Section title="Recuperacao esperada" items={plan.conservativeTreatment.expectedRecovery} />
            <Section title="Criterios de falha" items={plan.conservativeTreatment.failureCriteria} />
            <Section title="Conversao para cirurgia" items={plan.conservativeTreatment.conversionToSurgery} />
          </div>
        ) : null}
        {active === "technique-1" && plan.surgicalTechniques[0] ? <TechniquePanel technique={plan.surgicalTechniques[0]} labels={labels} /> : null}
        {active === "technique-2" && plan.surgicalTechniques[1] ? <TechniquePanel technique={plan.surgicalTechniques[1]} labels={labels} /> : null}
        {active === "postoperative" ? (
          <div className="grid gap-3 lg:grid-cols-2">
            <Section title="Imobilizacao" items={plan.postoperativeCare.immobilization} />
            <Section title="Amplitude de movimento" items={plan.postoperativeCare.rangeOfMotion} />
            <Section title="Fisioterapia" items={plan.postoperativeCare.physiotherapy} />
            <Section title="Carga" items={plan.postoperativeCare.weightBearing} />
            <Section title="Imagem de controle" items={plan.postoperativeCare.followUpImaging} />
            <Section title="Retorno funcional" items={plan.postoperativeCare.returnToFunction} />
            <Section title="Sinais de alerta" items={plan.postoperativeCare.redFlags} wide />
          </div>
        ) : null}
        {active === "plan-b" ? <Section title={labels.planB} items={plan.intraoperativeContingencyPlan} wide /> : null}
        {active === "differentials" ? <Section title={labels.differentials} items={plan.differentialDiagnosisAndAssociatedInjuries} wide /> : null}
        {active === "evidence" ? <EvidenceCards items={globalEvidence} relevanceLabel={labels.relevance} /> : null}
      </div>
    </div>
    </>
  );
}

export default SurgicalPlanningPremiumV2;
